/** Express Entry → Federal Skilled Worker synthesis (D-079): verdict, minimum requirements,
 * six-factor grid, proof of funds, CRS estimate, targeted categories and deadlines. Indicative. */

import { useImmigrationDossiersFswRetrieve, useImmigrationDossiersReportCreate } from "@agencyos/api-client";
import { formatXaf } from "@agencyos/core";
import { Badge, Button, GlassCard, Skeleton } from "@agencyos/ui-web";
import { CircleAlert, CircleCheck, CircleHelp, CircleX } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { localized } from "../../components/TranslatedFields";
import { SpecimenResult } from "../DocumentsPage";

type Result = "ok" | "ko" | "unknown";
interface Check {
  key: string;
  result: Result;
  detail: Record<string, unknown>;
}
interface Scenario {
  crs: number;
  fsw_eligible: Result;
  fsw_grid: number;
  french_level: number | null;
  french_category: boolean | null;
}
interface Couple {
  current: Scenario;
  swapped: Scenario | null;
  missing: string[];
  recommended: "current" | "swap" | "equal" | "unknown";
}
interface Report {
  spouse: Couple | null;
  eligible: Result;
  requirements: Check[];
  grid: { factors: Record<string, number>; max: Record<string, number>; total: number; pass_mark: number; unknown: string[]; result: Result };
  funds: { family_size: number; required: number; currency: string; available: number | null; exempt: boolean; result: Result; effective_from: string };
  categories: { key: string; name: Record<string, string>; detail: Record<string, string>; active: boolean; result: Result }[];
  deadlines: { key: string; due: string; days_left: number; state: "ok" | "soon" | "expired" }[];
  crs: { total: number; breakdown: Record<string, number>; warnings: string[] };
  tables: { kind: string; version: number; status: string; source: string }[];
}

const ICONS: Record<Result, ReactNode> = {
  ok: <CircleCheck size={18} className="check__icon--ok" aria-hidden="true" />,
  ko: <CircleX size={18} className="check__icon--ko" aria-hidden="true" />,
  unknown: <CircleHelp size={18} className="check__icon--unknown" aria-hidden="true" />,
};
const TONE = { ok: "success", ko: "danger", unknown: "default" } as const;

function Meter({ value, max, reached }: { value: number; max: number; reached?: boolean }) {
  const ratio = max > 0 ? Math.min(value / max, 1) : 0;
  return (
    <div className="meter__bar" role="presentation">
      <div className={`meter__fill${reached ? " meter__fill--ok" : ""}`} style={{ transform: `scaleX(${ratio})` }} />
    </div>
  );
}

/** D-081: both scenarios of the couple, the recommended principal applicant first. */
function CoupleCard({ couple }: { couple: Couple }) {
  const { t } = useTranslation();
  const rows: { key: string; value: (s: Scenario) => ReactNode }[] = [
    { key: "eligible", value: (s) => <Badge tone={TONE[s.fsw_eligible]}>{t(`fsw.result.${s.fsw_eligible}`)}</Badge> },
    { key: "grid", value: (s) => `${s.fsw_grid} / 100` },
    { key: "crs", value: (s) => <strong>{s.crs}</strong> },
    { key: "french", value: (s) => (s.french_level === null ? "—" : `NCLC ${s.french_level}`) },
    {
      key: "category",
      value: (s) => (s.french_category === null ? "—" : <Badge tone={s.french_category ? "success" : "default"}>{s.french_category ? t("common.yes") : t("common.no")}</Badge>),
    },
  ];
  return (
    <GlassCard as="section" className="stack">
      <div className="section-title">
        <div>
          <h2>{t("fsw.couple.title")}</h2>
          <p className="muted">{t("fsw.couple.intro")}</p>
        </div>
        <Badge tone={couple.recommended === "swap" ? "offer" : couple.recommended === "unknown" ? "default" : "success"}>
          {t(`fsw.couple.recommended.${couple.recommended}`)}
        </Badge>
      </div>
      {couple.swapped ? (
        <table className="table">
          <thead>
            <tr>
              <th />
              <th>{t("fsw.couple.current")}</th>
              <th>{t("fsw.couple.swapped")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{t(`fsw.couple.row.${row.key}`)}</td>
                <td>{row.value(couple.current)}</td>
                <td>{row.value(couple.swapped as Scenario)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">{t("fsw.couple.missing", { fields: couple.missing.map((f) => t(`strategy.spouseField.${f}`)).join(", ") })}</p>
      )}
    </GlassCard>
  );
}

/** Values the server compared, ready for the sentence (dates and amounts formatted). */
function useDetails() {
  const { i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  return (check: Check): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(check.detail)) {
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        out[key] = new Date(value).toLocaleDateString(locale, { timeZone: "Africa/Douala" });
      } else if (typeof value === "number" && (key === "required" || key === "available")) {
        out[key] = formatXaf(Math.round(value), { suffix: false });
      } else {
        out[key] = value === null || value === undefined ? "—" : String(value);
      }
    }
    return out;
  };
}

export function Synthesis({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const report = useImmigrationDossiersFswRetrieve(id, { query: { staleTime: 0 } });
  const pdf = useImmigrationDossiersReportCreate();
  const [documentId, setDocumentId] = useState<string | null>(null);
  const details = useDetails();
  if (report.isPending) return <Skeleton style={{ height: 240 }} />;
  if (report.data?.status !== 200) return <GlassCard role="alert">{t("common.error")}</GlassCard>;
  const r = report.data.data as unknown as Report;
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  const date = (iso: string) => new Date(iso).toLocaleDateString(locale, { timeZone: "Africa/Douala" });
  const unverified = r.tables.filter((table) => table.status !== "verified");
  // Same digit grouping as the FCFA amounts, with the currency of the table (CAD).
  const money = (amount: number) => `${formatXaf(Math.round(amount), { suffix: false })} ${r.funds.currency}`;

  return (
    <div className="stack">
      {unverified.length ? (
        <p role="alert" className="notice-warning ao-glass ao-card">
          <CircleAlert size={16} aria-hidden="true" /> {t("fsw.tablesNotVerified")}
        </p>
      ) : null}

      <GlassCard as="section" className="stack">
        <div className="row between">
          <span />
          <Button
            variant="secondary"
            disabled={pdf.isPending}
            onClick={() =>
              pdf.mutate(
                { id, data: { language: i18n.language === "en" ? "en" : "fr" } },
                { onSuccess: (response) => response.status === 201 && setDocumentId(response.data.id) },
              )
            }
          >
            {t("fsw.reportPdf")}
          </Button>
        </div>
        {documentId ? <SpecimenResult id={documentId} /> : null}
        <div className="section-title">
          <div>
            <h2>{t("fsw.verdictTitle")}</h2>
            <p className="muted">{t("fsw.verdictIntro")}</p>
          </div>
          <Badge tone={TONE[r.eligible]}>{t(`fsw.verdict.${r.eligible}`)}</Badge>
        </div>
        <ul className="checks">
          {r.requirements.map((check) => (
            <li key={check.key} className="check">
              {ICONS[check.result]}
              <span>
                <strong>{t(`fsw.req.${check.key}`)}</strong>
                <span className="check__detail"> · {t(`fsw.reqHelp.${check.key}`)}</span>
                {check.result === "unknown" ? (
                  <span className="check__detail"> · {t("fsw.missing")}</span>
                ) : (
                  <span className="check__detail"> · {t(`fsw.reqDetail.${check.key}`, details(check))}</span>
                )}
              </span>
              <Badge tone={TONE[check.result]}>{t(`fsw.result.${check.result}`)}</Badge>
            </li>
          ))}
        </ul>
      </GlassCard>

      {r.spouse ? <CoupleCard couple={r.spouse} /> : null}

      <div className="grid">
        <GlassCard as="section" className="stack">
          <div className="section-title">
            <h2>{t("fsw.gridTitle")}</h2>
            <span className="big-number">
              {r.grid.total}
              <span className="muted"> / 100</span>
            </span>
          </div>
          <div className="meter">
            <Meter value={r.grid.total} max={100} reached={r.grid.total >= r.grid.pass_mark} />
            <span className="check__detail">{t("fsw.passMark", { mark: r.grid.pass_mark })}</span>
          </div>
          <table className="table">
            <tbody>
              {Object.entries(r.grid.factors).map(([factor, points]) => (
                <tr key={factor}>
                  <td>
                    {t(`fsw.factor.${factor}`)}
                    {r.grid.unknown.includes(factor) ? <span className="check__detail"> · {t("fsw.result.unknown")}</span> : null}
                  </td>
                  <td style={{ width: "40%" }}>
                    <Meter value={points} max={r.grid.max[factor] ?? 1} />
                  </td>
                  <td className="nowrap">
                    <strong>{points}</strong>
                    <span className="muted"> / {r.grid.max[factor]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>

        <div className="stack">
          <GlassCard as="section" className="stack">
            <div className="section-title">
              <h2>{t("fsw.crsTitle")}</h2>
              <span className="big-number">{r.crs.total}</span>
            </div>
            <p className="muted">{t("fsw.crsIntro")}</p>
          </GlassCard>
          <GlassCard as="section" className="stack">
            <div className="section-title">
              <h2>{t("fsw.fundsTitle")}</h2>
              <Badge tone={TONE[r.funds.result]}>{t(`fsw.result.${r.funds.result}`)}</Badge>
            </div>
            <table className="table">
              <tbody>
                <tr>
                  <td>{t("fsw.familySize")}</td>
                  <td>
                    <strong>{r.funds.family_size}</strong>
                  </td>
                </tr>
                <tr>
                  <td>{t("fsw.required")}</td>
                  <td>
                    <strong>{money(r.funds.required)}</strong>
                  </td>
                </tr>
                <tr>
                  <td>{t("fsw.available")}</td>
                  <td>{r.funds.available === null ? <span className="muted">{t("fsw.missing")}</span> : money(r.funds.available)}</td>
                </tr>
              </tbody>
            </table>
            {r.funds.exempt ? <p className="ok">{t("fsw.fundsExempt")}</p> : null}
            <p className="check__detail">{t("fsw.fundsNote", { date: r.funds.effective_from ? date(r.funds.effective_from) : "—" })}</p>
          </GlassCard>
        </div>
      </div>

      <div className="grid">
        <GlassCard as="section" className="stack">
          <h2>{t("fsw.categoriesTitle")}</h2>
          <p className="muted">{t("fsw.categoriesIntro")}</p>
          <ul className="checks">
            {r.categories.map((category) => (
              <li key={category.key} className="check">
                {ICONS[category.result]}
                <span>
                  <strong>{localized(category.name, i18n.language)}</strong>
                  {category.active ? null : <span className="check__detail"> · {t("fsw.inactive")}</span>}
                  <span className="check__detail"> · {localized(category.detail, i18n.language)}</span>
                </span>
                <Badge tone={TONE[category.result]}>{t(`fsw.result.${category.result}`)}</Badge>
              </li>
            ))}
          </ul>
        </GlassCard>
        <GlassCard as="section" className="stack">
          <h2>{t("fsw.deadlinesTitle")}</h2>
          {r.deadlines.length ? (
            <table className="table">
              <tbody>
                {r.deadlines.map((d) => (
                  <tr key={d.key}>
                    <td>{t(`fsw.deadline.${d.key}`)}</td>
                    <td>{date(d.due)}</td>
                    <td>
                      <Badge tone={d.state === "expired" ? "danger" : d.state === "soon" ? "offer" : "success"}>
                        {d.state === "expired" ? t("fsw.expired") : t("fsw.daysLeft", { count: d.days_left })}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="muted">{t("fsw.noDeadline")}</p>
          )}
        </GlassCard>
      </div>
      <p role="note" className="check__detail">
        {t("fsw.disclaimer")}
      </p>
    </div>
  );
}
